import FalconCardHeader from '../../../../../components/common/FalconCardHeader';
import React, { useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Card, Col, Form, Row } from "react-bootstrap";
import CardDropdown from "../../../../../components/common/CardDropdown";
import TemperaturaChart from "./TemperaturaChart"; // Assuming this chart component will handle temperature visualization
import Flex from "../../../../../components/common/Flex";

const groupByDate = (data) => {
    const grouped = {};
    data.forEach((alert) => {
        const formattedDate = dayjs(alert.fecha, "YYYY/MM/DD HH:mm:ss").format(
            "YYYY-MM-DD"
        );
        if (!grouped[formattedDate]) {
            grouped[formattedDate] = [];
        }
        grouped[formattedDate].push(alert);
    });
    return grouped;
};

const groupByHour = (data) => {
    const grouped = {};
    data.forEach((alert) => {
        const formattedHour = dayjs(alert.fecha, "YYYY/MM/DD HH:mm:ss").format(
            "HH:00"
        );
        if (!grouped[formattedHour]) {
            grouped[formattedHour] = [];
        }
        grouped[formattedHour].push(alert);
    });
    return grouped;
};

const calcularPromediosTemperatura = (data) => {
    const promedios = [];
    const temperaturas = [];
    for (const fecha in data) {
        const registros = Array.isArray(data[fecha]) ? data[fecha] : [];
        if (registros.length === 0) continue;
        let promedios_dia = [];
        registros.forEach((p) => {
            // Assuming 'temperatura' is a property in your data objects
            // You might need to adjust the conditions below if your temperature data has specific types or motor types
            if ((p.tipo === 1 || p.tipo === 2 || p.tipo === 4 || p.tipo === 6) && p.tipoMotor === 1) {
                promedios_dia.push(p.temperatura);
                temperaturas.push(p.temperatura);
            } else if ((p.tipo === 3 || p.tipo === 5) && p.tipoMotor === 0) {
                promedios_dia.push(p.temperatura);
                temperaturas.push(p.temperatura);
            }
        });
        const totalTemperatura = promedios_dia.reduce((sum, item) => sum + item, 0);
        const promedio = promedios_dia.length > 0 ? totalTemperatura / promedios_dia.length : 0;
        promedios.push(promedio);
    }
    const promTempDiaria =
        temperaturas.length > 0
            ? (temperaturas.reduce((sum, item) => sum + item, 0) / temperaturas.length).toFixed(2)
            : 0;
    return { promedios, promTempDiaria };
};

const calcularTempMaxima = (data) => {
    const maximasPorDia = [];
    let maximaAbsoluta = 0; // This variable is declared but not used, consider removing if not needed.

    for (const fecha in data) {
        const registros = Array.isArray(data[fecha]) ? data[fecha] : [];
        if (registros.length === 0) {
            maximasPorDia.push(0); // No grafica nada para ese día
            continue;
        }

        const temperaturas = registros
            .map(registro => registro.temperatura) // Change 'velocidad' to 'temperatura'
            .filter(v => !isNaN(v));

        if (temperaturas.length === 0) {
            maximasPorDia.push(0); // No grafica si no hay temperaturas válidas
            continue;
        }

        const maximaDia = Math.max(...temperaturas);
        maximasPorDia.push(maximaDia);
    }
    const tempMax = Math.max(...maximasPorDia);

    return { maximasPorDia: maximasPorDia, promTempMax: tempMax };
};

const calcularMaximosConfiguracion = (data) => {
    const temperaturasMaximas = [];
    for (const fecha in data) {
        const vehiculos = Array.isArray(data[fecha]) ? data[fecha] : [];
        if (vehiculos.length === 0) continue;
        const totalMaxima = vehiculos.reduce(
            (sum, vehiculo) => sum + (vehiculo.vehiculos?.temperaturaMaxima || 0), // Assuming 'temperaturaMaxima' for configuration
            0
        );
        const maxima = vehiculos.length > 0 ? Math.round(totalMaxima / vehiculos.length) : 0;
        temperaturasMaximas.push(maxima);
    }
    const tempMaxConfig =
        temperaturasMaximas.length > 0
            ? Math.round(
                temperaturasMaximas.reduce((sum, item) => sum + item, 0) /
                temperaturasMaximas.length
            )
            : 0;
    return { maximos: temperaturasMaximas, tempMaxConfig };
};

const Temperatura = ({ data }) => {

    const chartRef = useRef(null);

    // Agrupar alertas
    const groupedByDate = useMemo(() => groupByDate(data), [data]);
    const groupedByHour = useMemo(() => groupByHour(data), [data]);
    const uniqueDates = Object.keys(groupedByDate);
    const isSingleDay = uniqueDates.length === 1;
    const chartData = isSingleDay ? groupedByHour : groupedByDate;

    // Calcular promedios y máximos de temperatura
    const { promedios, promTempDiaria } = useMemo(() => calcularPromediosTemperatura(chartData), [chartData]);
    const { maximasPorDia, promTempMax } = useMemo(() => calcularTempMaxima(chartData), [chartData]);
    const { maximos, tempMaxConfig } = useMemo(() => calcularMaximosConfiguracion(chartData), [chartData]);

    const handleLegendToggle = (name) => {
        chartRef.current.getEchartsInstance().dispatchAction({
            type: "legendToggleSelect",
            name,
        });
    };

    let tempPromedioStr = `${` ${promTempDiaria}`} °C`;
    let tempMaxStr = `${` ${promTempMax}`} °C`;
    let tempMaxConfigStr = `${` ${tempMaxConfig}`} °C`;

    return (
        <Card className="h-100">
            <FalconCardHeader
                title={"Temperatura"}
                light
                titleTag="h6"
            ></FalconCardHeader>

            <Card.Body className="flex-grow-1 pe-xxl-0" style={{ minHeight: '400px' }}>
                <TemperaturaChart
                    data={chartData}
                    ref={chartRef}
                    style={{ height: '90%', width: '100%' }}
                />
            </Card.Body>
        </Card>
    );
};

export default Temperatura;